import { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form-v7';
import type { Resolver } from 'react-hook-form-v7';
import { isValidPhoneNumber } from 'react-phone-number-input';

// Straight at the schema module and not at the barrel of the steps: that barrel reaches back into
// these hooks, and the cycle would leave one of the two undefined at import time.
import { createPhoneFormSchema } from '@identity/presentation/components/specific/steps/PhoneStep.schema';
import type { PhoneFormValues } from '@identity/presentation/components/specific/steps/PhoneStep.schema';

export type UseProfileContactFormParams = {
  registeredPhone?: string;
  isPhoneVerified?: boolean;
};

/**
 * What the phone badge shows for the value currently in the field. `undefined` hides it.
 *
 * A number under construction is not a fact yet: saying "sin verificar" of `+52 55` is true and
 * useless, so nothing is shown until the number is complete.
 */
export const phoneBadgeFor = ({
  typedPhone,
  registeredPhone,
  isPhoneVerified,
}: {
  typedPhone: string;
  registeredPhone?: string;
  isPhoneVerified?: boolean;
}): boolean | undefined => {
  if (!typedPhone || !isValidPhoneNumber(typedPhone)) return undefined;

  // A number different from the registered one cannot be verified, whatever the map says.
  return typedPhone === registeredPhone ? isPhoneVerified : false;
};

/**
 * The contact form of Mi perfil: the phone field, the badge it derives and whether the number can be
 * taken to the verification screen.
 *
 * The button is enabled by two separate reasons, and the second one is not an edge case. Changing
 * the number always enables it; so does a number registered but never verified, because with the
 * gate switched off Mi perfil is the only way that account has to verify itself, and without this
 * term it would find the button dead.
 */
export const useProfileContactForm = ({ registeredPhone, isPhoneVerified }: UseProfileContactFormParams) => {
  const resolver = useMemo(() => (yupResolver(createPhoneFormSchema()) as unknown) as Resolver<PhoneFormValues>, []);

  const form = useForm<PhoneFormValues>({
    defaultValues: { phone: registeredPhone ?? '' },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver,
  });

  const { reset } = form;

  // The registered number can arrive after this form is mounted: coming back from the verification
  // the cache answers with the old one and the refetch lands a render later. Default values are read
  // once, so without this the field would go on showing the number that just stopped being true, and
  // the badge would call the new one unverified for differing from it. Resetting and not setting the
  // value also moves the baseline, so a number that only just changed does not count as a change.
  useEffect(() => {
    reset({ phone: registeredPhone ?? '' });
  }, [registeredPhone, reset]);

  const typedPhone = form.watch('phone');
  const isPhoneComplete = Boolean(typedPhone) && isValidPhoneNumber(typedPhone);
  const hasChanged = typedPhone !== registeredPhone;

  return {
    form,
    typedPhone,
    phoneBadge: phoneBadgeFor({ typedPhone, registeredPhone, isPhoneVerified }),
    isSubmitDisabled: !isPhoneComplete || (!hasChanged && isPhoneVerified !== false),
  };
};
